// 短期握手上下文只留内存；同标签页的引用不含凭据或正文，刷新恢复必须重新查询 Java 目标。
const contexts = new Map()
const prefix = 'biointelos:message-navigation:'
let generation = 0
const validRequest = value => typeof value === 'string' && /^[A-Za-z0-9-]{1,80}$/.test(value)
const validId = value => Number.isSafeInteger(value) && value > 0
function safeRoute(value) {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//') || ['\\', '\0', '\r', '\n'].some(part => value.includes(part))) return '/billing'
  try {
    const url = new URL(value, 'https://message-return.invalid')
    const credentials = ['token', 'gutdbtoken', 'accesstoken', 'refreshtoken', 'authorization', 'jwt', 'secret', 'password', 'code', 'shortcode', 'resumetoken', 'claimtoken']
    // 分享领取长链接和 /g/:code 短链接均带领取凭据，只保留受控的账单返回入口。
    if (/^\/g\//i.test(decodeURIComponent(url.pathname))
      || [...url.searchParams.keys()].some(key => {
        const name = key.replace(/[_-]/g, '').toLowerCase()
        return credentials.includes(name) || name.endsWith('token')
      })) return '/billing'
    return value
  } catch { return '/billing' }
}
export const messageNavigationError = message => Object.assign(new Error(message), { code: 'MESSAGE_NAVIGATION_INVALID', terminal: true })
function storage() { try { return window.sessionStorage } catch { return null } }
function targetKey(target) {
  if (!target || !['SESSION_REQUEST', 'SESSION_MESSAGE', 'NOTEBOOK', 'RESEARCH_SHARE'].includes(target.type)) throw messageNavigationError('原消息目标无效，请返回消息重新打开')
  const values = ['objectId', 'analysisId', 'runId', 'sessionId', 'requestId', 'messageId', 'version', 'checkpointId', 'workspaceKey', 'section'].map(key => {
    const value = target[key]
    if (value == null) return null
    if (typeof value !== 'string' || !/^[A-Za-z0-9_.:-]{1,2048}$/.test(value)) throw messageNavigationError('原消息目标无效，请返回消息重新打开')
    return value
  })
  return JSON.stringify([target.type, ...values])
}
function viewState(value = {}) {
  return { originRoute: safeRoute(value.originRoute), category: ['', 'WORK', 'PLATFORM'].includes(value.category) ? value.category : '',
    pendingOnly: value.pendingOnly === true, unreadOnly: value.unreadOnly === true, loadedIds: Array.isArray(value.loadedIds) ? [...new Set(value.loadedIds.filter(validId))] : [],
    cursor: typeof value.cursor === 'string' ? value.cursor : null, hasMore: value.hasMore === true,
    scrollTop: Number.isFinite(value.scrollTop) && value.scrollTop >= 0 ? value.scrollTop : 0,
    listedThroughId: validId(value.listedThroughId) ? value.listedThroughId : 0,
    detailFilter: { category: ['', 'WORK', 'PLATFORM'].includes(value.detailFilter?.category) ? value.detailFilter.category : undefined,
      pendingOnly: value.detailFilter?.pendingOnly === true, unreadOnly: value.detailFilter?.unreadOnly === true } }
}
export function registerMessageNavigation(context) {
  contexts.set(context.requestId, { ...context, createdAt: Date.now() })
  if (!validRequest(context.requestId) || !validId(context.messageId) || context.identityId == null) return
  try {
    const reference = { schema: 1, requestId: context.requestId, messageId: context.messageId, identityId: String(context.identityId),
      directorySlug: context.navigation.directorySlug || '', targetKey: targetKey(context.navigation.target), returnState: viewState(context.returnState) }
    storage()?.setItem(prefix + context.requestId, JSON.stringify(reference))
  } catch { /* 禁用存储时保留本页导航；刷新后明确拒绝，不回退普通 Code。 */ }
}
export function readMessageNavigation(requestId, identityId) {
  const context = contexts.get(requestId)
  return context && String(context.identityId) === String(identityId) && Date.now() - context.createdAt < 120000 ? context : null
}
export function readMessageNavigationReference(requestId, identityId) {
  if (!validRequest(requestId) || identityId == null) return null
  try {
    const value = JSON.parse(storage()?.getItem(prefix + requestId) || 'null')
    if (value?.schema !== 1 || value.requestId !== requestId || value.identityId !== String(identityId) || !validId(value.messageId)
      || typeof value.directorySlug !== 'string' || !/^[A-Za-z0-9_-]{0,2048}$/.test(value.directorySlug)
      || typeof value.targetKey !== 'string' || value.targetKey.length > 20000) return null
    return { requestId, messageId: value.messageId, identityId: value.identityId, directorySlug: value.directorySlug,
      targetKey: value.targetKey, returnState: viewState(value.returnState) }
  } catch { return null }
}
function matchesRoute(navigation, query) {
  const target = navigation.target
  return target?.type === 'RESEARCH_SHARE' ? query.dir === undefined && query.session === undefined
    : navigation.directorySlug === query.dir && target?.sessionId === query.session
}
function matchesResolvedTarget(referenceKey, target) {
  const nextKey = targetKey(target)
  if (referenceKey === nextKey) return true
  // 完成原请求后，Java只把同一请求切为只读历史入口；这不是换到另一条消息。
  // 其余身份、目录、会话、请求、消息和版本仍逐项匹配，禁止借完成状态扩大目标。
  try {
    const previous = JSON.parse(referenceKey)
    if (!Array.isArray(previous) || previous.length !== 11 || previous[0] !== 'SESSION_REQUEST'
      || typeof previous[5] !== 'string' || !previous[5] || target?.type !== 'SESSION_MESSAGE') return false
    const history = [...previous]
    history[0] = 'SESSION_MESSAGE'; history[1] = previous[5]; history[8] = null; history[10] = 'request-history'
    return JSON.stringify(history) === nextKey
  } catch { return false }
}
export async function restoreMessageNavigation(requestId, identityId, query, loadTarget, signal, isCurrent = () => true) {
  if (!validRequest(requestId) || (query.messageReturn !== undefined && query.messageReturn !== requestId)) throw messageNavigationError('原消息导航引用不一致，请返回消息重新打开')
  const current = readMessageNavigation(requestId, identityId)
  if (current) {
    if (!matchesRoute(current.navigation, query)) throw messageNavigationError('原消息目标与当前页面不一致，请返回消息重新打开')
    return current
  }
  const reference = readMessageNavigationReference(requestId, identityId)
  if (!reference || query.messageReturn !== requestId) throw messageNavigationError('原消息导航引用已失效或身份已变化，请返回消息重新打开')
  const epoch = generation
  let navigation
  try { navigation = await loadTarget(reference.messageId, signal) }
  catch { throw messageNavigationError('暂时无法重新核实原消息，请重试导航或返回消息') }
  if (signal?.aborted || epoch !== generation || !isCurrent()) throw messageNavigationError('身份或页面已变化，原消息导航已取消')
  if (!navigation?.available) throw messageNavigationError(navigation?.reason || '原消息目标暂不可访问，请返回消息重新打开')
  if (reference.directorySlug !== (navigation.directorySlug || '')) throw messageNavigationError('原会话位置已变化，请返回消息重新打开')
  if (!matchesResolvedTarget(reference.targetKey, navigation.target)) throw messageNavigationError('原消息定位信息已变化，请返回消息重新打开')
  if (!matchesRoute(navigation, query)) throw messageNavigationError('原消息目标与当前页面不一致，请返回消息重新打开')
  registerMessageNavigation({ ...reference, identityId, navigation })
  return readMessageNavigation(requestId, identityId)
}
export function clearMessageNavigations() {
  generation++; contexts.clear()
  const value = storage()
  try { if (value) for (const key of Object.keys(value)) if (key.startsWith(prefix)) value.removeItem(key) } catch { /* 存储不可用时不跨身份复用内存。 */ }
}
